/**
 * Phase 6e.4.2-hotfix Bug B — payment schedule re-anchored at
 * `firstPaymentDate`.
 *
 * Backend semantics (single source of truth, sealed Phase 4-5,
 * confirmed in `core/fx.ts:178` + `calculator.ts:66`):
 *
 *   For period i = 0..N-1 (N = ceil(leaseTermMonths / monthsPerPeriod)):
 *     payment_date[i] = addMonths(firstPaymentDate, i × monthsPerPeriod)
 *
 * The previous (Phase 6a.23 / Bulgu AJ) formula derived dates from
 * `commencementDate + (period − 1 + timingOffset) × monthsPerPeriod`.
 * That coincided with the backend for MONTHLY (because the auditor's
 * `firstPaymentDate` for IN_ARREARS MONTHLY is typically
 * `commencement + 1 month`) but diverged by `(monthsPerPeriod − 1)`
 * months for every QUARTERLY / SEMI_ANNUAL / ANNUAL contract — and the
 * backend rejected calculation with "paymentDateRates: missing entry
 * for payment date YYYY-MM-DD".
 *
 * IFRS 16 framing: §22 defines `commencementDate` as the asset-delivery
 * date (drives the amortization grid); §B34 defines `lease payments` as
 * the contractually specified cash flows (drives the payment grid).
 * The two grids share a denominator (months) but are anchored on
 * different dates.
 *
 * Day-of-month logic: anchor to `firstPaymentDate`'s day-of-month. When
 * a target month is shorter (e.g. anchor day 31, target Feb), clamp to
 * the last day of the target month. Mirrors backend's
 * `addMonths()` convention.
 *
 * `timing` stays on the interface (callers + tests reference it and
 * removing it would cascade) but the implementation no longer reads it
 * — once `firstPaymentDate` is set the timing label is implicit
 * (IN_ARREARS ⇒ firstPaymentDate = commencement + monthsPerPeriod,
 * IN_ADVANCE ⇒ firstPaymentDate = commencement; either way the
 * cash-flow grid steps from firstPaymentDate).
 */

import type { PaymentFrequency, PaymentTiming } from "@/types";

const MONTHS_PER_PAYMENT: Record<PaymentFrequency, number> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  SEMI_ANNUAL: 6,
  ANNUAL: 12,
};

export interface GenerateDatesInput {
  /**
   * Asset-delivery date (IFRS 16 §22). Used as the lower-bound guard
   * (firstPaymentDate must be >= commencementDate) and indirectly via
   * `leaseTermMonths` to bound the payment count. NOT used to compute
   * individual payment dates.
   */
  commencementDate: string; // ISO YYYY-MM-DD
  /**
   * Contractual first-payment date (IFRS 16 §B34). REQUIRED. Every
   * subsequent date is `addMonths(firstPaymentDate, i × monthsPerPeriod)`
   * for i = 0..N-1.
   *
   * Pre-hotfix the helper computed the schedule from `commencementDate`
   * and silently disagreed with the backend for QUARTERLY /
   * SEMI_ANNUAL / ANNUAL contracts. Backend is canonical.
   */
  firstPaymentDate: string; // ISO YYYY-MM-DD
  leaseTermMonths: number;
  frequency: PaymentFrequency;
  /**
   * Reserved on the interface for callers + tests. No longer drives the
   * formula — once `firstPaymentDate` is correct the timing label is
   * implicit. Removing it from the interface would cascade through
   * every call site beyond this hotfix's scope.
   */
  timing: PaymentTiming;
  /**
   * BB4 / BI — optional upper bound. When supplied (ISO YYYY-MM-DD),
   * dates strictly after this date are dropped so the bulk-generate
   * action populates only payment dates that fall inside the auditor's
   * audit window. Manual "+ Satır Ekle" insertions outside the window
   * remain possible — this is a bulk-generation cap, not a hard schema
   * rule.
   */
  auditPeriodEnd?: string;
}

export function generatePaymentDates(
  input: GenerateDatesInput,
): string[] {
  const { commencementDate, firstPaymentDate, leaseTermMonths, frequency } =
    input;

  if (!commencementDate || !/^\d{4}-\d{2}-\d{2}$/.test(commencementDate)) {
    return [];
  }
  if (!firstPaymentDate || !/^\d{4}-\d{2}-\d{2}$/.test(firstPaymentDate)) {
    return [];
  }
  // Data-integrity guard: a first-payment date earlier than commencement
  // is contractually impossible (the asset isn't yet delivered).
  if (firstPaymentDate < commencementDate) return [];
  if (
    !Number.isInteger(leaseTermMonths) ||
    leaseTermMonths < 1 ||
    leaseTermMonths > 1200
  ) {
    return [];
  }

  const upperBound =
    input.auditPeriodEnd && /^\d{4}-\d{2}-\d{2}$/.test(input.auditPeriodEnd)
      ? input.auditPeriodEnd
      : null;
  // If the very first payment is past the audit window there is nothing
  // to enumerate — every period would be proforma.
  if (upperBound !== null && firstPaymentDate > upperBound) return [];

  const monthsPerPayment = MONTHS_PER_PAYMENT[frequency];
  // Total payments = ceil(leaseTermMonths / monthsPerPayment). Lease
  // length is measured from commencement, so payments past
  // commencement + leaseTermMonths shouldn't be enumerated. Only the
  // date anchor changes — the payment count is unchanged from prior.
  const totalPayments = Math.ceil(leaseTermMonths / monthsPerPayment);

  const [yStr, mStr, dStr] = firstPaymentDate.split("-");
  const startY = Number(yStr);
  const startM = Number(mStr) - 1; // JS months are 0-indexed
  const anchorDay = Number(dStr);

  const out: string[] = [];
  for (let i = 0; i < totalPayments; i++) {
    const offsetMonths = i * monthsPerPayment;
    const targetY = startY + Math.floor((startM + offsetMonths) / 12);
    const targetM = (startM + offsetMonths) % 12;
    // End-of-month clamping — `new Date(Y, M+1, 0)` returns the last
    // day of month M.
    const daysInTargetMonth = new Date(
      Date.UTC(targetY, targetM + 1, 0),
    ).getUTCDate();
    const day = Math.min(anchorDay, daysInTargetMonth);
    const dt = new Date(Date.UTC(targetY, targetM, day));
    const iso = `${dt.getUTCFullYear().toString().padStart(4, "0")}-${(
      dt.getUTCMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${dt.getUTCDate().toString().padStart(2, "0")}`;
    if (upperBound !== null && iso > upperBound) break;
    out.push(iso);
  }
  return out;
}
